import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Form, Spin } from 'antd';
import ChartComponent, { CHART_TYPES } from '../../../components/chartComponent/ChartComponent';

import { ApexOptions } from 'apexcharts';
import TextArea from 'antd/es/input/TextArea';
import { REGEX_ALL_ALLOWED_SAFE_FILTER } from '../../../constants/regex';
import { sanitizeInput } from '../../../utils/sanitizeInput';

export interface IPriceVsVarianceProps {
  establishment: string;
  priceVarianceArr: priceVariance[];
  establishmentId: number;
  descriptionFieldName: string;
  mode: 'view' | 'add';
}

interface priceVariance {
  product: string;
  startDatePrice: number;
  endDatePrice: number;
  variance: number;
}

/* const sanitizeInput = (str: string) => {
  if (!str) return '';
  return str.replace(/[&<>"'/]/g, (char) => {
    const entities: { [key: string]: string } = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
      '/': '&#x2F;',
    };
    return entities[char] || char;
  });
}; */

const PriceVsVarianceChartApex: React.FC<IPriceVsVarianceProps> = ({
  establishment,
  priceVarianceArr,
  establishmentId,
  descriptionFieldName,
  mode,
}) => {
  const [priceVariance, setPriceVarianceArr] = useState<priceVariance[]>(priceVarianceArr);
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation(['newsletter']);

  const chartData = priceVariance;

  const products = chartData.map((item) => item.product);

  const series = [
    {
      name: t('startPrice'),
      type: 'column',
      data: chartData.map((item) => (item?.startDatePrice ? item?.startDatePrice.toFixed(2) : 0)),
    },
    {
      name: t('endPrice'),
      type: 'column',
      data: chartData.map((item) => (item?.endDatePrice ? item?.endDatePrice.toFixed(2) : 0)),
    },
    {
      name: t('variance'),
      type: 'line',
      data: chartData.map((item) => (item?.variance ? item?.variance.toFixed(2) : 0)),
    },
  ];

  // Options for the chart
  const options: ApexOptions = {
    chart: {
      // height: 500,
      type: 'line',
      stacked: false,
    },
    dataLabels: {
      enabled: true,
      enabledOnSeries: [2],
    },
    plotOptions: {
      bar: {
        columnWidth: '50%',
      },
    },
    stroke: {
      width: [0, 0, 4],
      curve: 'smooth',
    },
    yaxis: [
      {
        seriesName: t('startPrice'),
        axisTicks: { show: true },
        axisBorder: { show: true },
        title: { text: t('price') },
        labels: { style: { fontSize: '12px' } },
      },
      { seriesName: t('startPrice'), show: false },
      {
        opposite: true,
        seriesName: t('variance'),
        axisTicks: { show: true },
        axisBorder: { show: true },
        title: { text: t('variance') },
        labels: { style: { fontSize: '12px' } },
      },
    ],
    legend: {
      position: 'bottom',
      horizontalAlign: 'center',
      floating: false,
      show: true,
      // offsetX: 40,
      // width: 300,
    },
    xaxis: {
      categories: chartData.map((item) => sanitizeInput(item.product)),
      labels: { rotate: -35, trim: true, rotateAlways: true, style: { fontSize: '12px' } },
    },
    tooltip: {
      shared: true,
      intersect: false,
    },
  };

  return (
    <>
      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <Spin size="large" />
        </div>
      ) : (
        <div className="mt-10 space-y-6" id={`chart-${establishmentId}`}>
          <div className="border-t-2 border-gray-200 pt-4">
            {mode !== 'view' ? (
              <>
                <h6 className="mb-3 text-3xl font-semibold text-gray-800">{establishment}</h6>
                <h4 className="mb-3 font-semibold text-gray-800">{t('priceVsVariance')}</h4>
              </>
            ) : (
              <>
                <h6 className="mb-3 text-2xl font-semibold text-gray-800">
                  {t('establishmentTile', { establishmentName: establishment })}
                </h6>
              </>
            )}

            {chartData.length > 0 ? (
              <div
                className={`${mode !== 'view' ? '[&_.apexcharts-legend-group]:!flex-row' : '!flex !flex-row !items-center !justify-center'}`}
              >
                <ChartComponent
                  title=""
                  type={CHART_TYPES.LINE}
                  series={series}
                  options={options}
                  height={500}
                  loading={loading}
                  // maxHeight={300}
                />
                {/* <ReactApexChart
                    options={options}
                    series={series}
                    type="line"
                    height={350}
                  /> */}
              </div>
            ) : (
              <div className="py-8 text-center text-gray-500">{t('noPriceVariance')}</div>
            )}

            <div>
              {mode !== 'view' && (
                /*  (
                <div className="chart-description">
                  <h5 className="!font-semibold">
                    {t('establishmentDescription', { establishmentName: establishment })}
                  </h5>
                  <p>{description}</p>
                </div>
              ) :  */
                <div>
                  <Form.Item
                    key={establishment}
                    name={descriptionFieldName}
                    label={t('establishmentDescription', { establishmentName: establishment })}
                    layout="vertical"
                    validateFirst
                    rules={[
                      { required: false },
                      {
                        validator: (_, value) => {
                          if (value && value.trim() === '') {
                            return Promise.reject(
                              t('establishmentDescriptionSpaceValidation', {
                                establishmentName: establishment,
                              })
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                      {
                        validator: (_, value) => {
                          if (!value) return Promise.resolve();
                          const regex = REGEX_ALL_ALLOWED_SAFE_FILTER;
                          return regex.test(value)
                            ? Promise.resolve()
                            : Promise.reject(t('invalidInput'));
                        },
                      },
                      {
                        validator: (_, value) => {
                          if (!value || value.trim() === '') {
                            return Promise.resolve();
                          }

                          const trimmedLength = value.replace(/\s/g, '').length;
                          if (trimmedLength < 1) {
                            return Promise.reject(
                              t('establishmentDescriptionMinValidation', {
                                establishmentName: establishment,
                              })
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                      {
                        validator: (_, value) => {
                          if (!value || value.trim() === '') {
                            return Promise.resolve();
                          }

                          const trimmedLength = value.replace(/\s/g, '').length;
                          if (trimmedLength > 200) {
                            return Promise.reject(
                              t('establishmentDescriptionMaxValidation', {
                                establishmentName: establishment,
                              })
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <TextArea
                      placeholder={t('establishmemtDescriptionPlaceholder', {
                        establishmentName: establishment,
                      })}
                      rows={5}
                    />
                  </Form.Item>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PriceVsVarianceChartApex;
